import React, { useState, useEffect } from "react";
import axios from "axios";
import { Editor } from "@monaco-editor/react";
import { X, Sparkles, ArrowLeft, Plus, Trash2, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import host from "../global";
import { useTheme } from "../hooks/useTheme";
import {
  APEX_TOPICS, APEX_DIFFICULTIES, DEFAULT_APEX_RUBRIC, APEX_STARTER_TEMPLATE,
} from "./orgadmin/coding/codingConstants";

const PHASE = { DESCRIBE: "describe", GENERATING: "generating", REVIEW: "review" };
const PHASE_LABELS = ["Describe", "Review & rubric"];

const blank = (s) => !s || s.trim().length === 0;
const cloneDefaultRubric = () => DEFAULT_APEX_RUBRIC.map((c) => ({ ...c }));

const DIFF_STYLE = {
  Easy: "border-emerald-300 text-emerald-600 dark:border-emerald-500/40 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10",
  Medium: "border-amber-300 text-amber-600 dark:border-amber-500/40 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10",
  Hard: "border-rose-300 text-rose-600 dark:border-rose-500/40 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10",
};

const inputCls = (invalid) =>
  `w-full dark:glass-effect bg-bgPrimary px-4 py-3 rounded-xl dark:text-white text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 placeholder:text-textSecondary border border-gray-200 dark:border-white/[0.08] ${invalid ? "ring-2 ring-red-400 border-red-400/40" : ""}`;

const validateDescribe = ({ title, description, topics }) => {
  const e = {};
  if (blank(title)) e.title = "Title is required";
  else if (title.trim().length < 3) e.title = "Must be at least 3 characters";
  if (blank(description)) e.description = "Description is required";
  else if (description.trim().length < 20) e.description = "Must be at least 20 characters";
  if (!topics.length) e.topics = "Select at least one topic";
  return e;
};

const rubricTotal = (rubric) => rubric.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);

const validateRubric = (rubric) => {
  if (!rubric.length) return "Add at least one rubric criterion.";
  if (rubric.some((c) => blank(c.name))) return "Every criterion needs a name.";
  if (rubric.some((c) => !(Number(c.weight) > 0))) return "Every criterion needs a weight above 0.";
  const total = rubricTotal(rubric);
  if (Math.round(total) !== 100) return `Weights must add up to 100 (currently ${Math.round(total)}).`;
  return "";
};

/**
 * Create-a-custom-Apex-problem modal.
 *   onSubmit(payload) -> Promise<{ ok: boolean, error?: string }>   (parent saves + refreshes the library)
 *   onClose()
 */
const HostApexProblem = ({ onSubmit, onClose }) => {
  const accessToken = sessionStorage.getItem("accessToken");
  const { theme } = useTheme();

  const [phase, setPhase] = useState(PHASE.DESCRIBE);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [topics, setTopics] = useState([]);
  const [starterCode, setStarterCode] = useState(APEX_STARTER_TEMPLATE);
  const [referenceSolution, setReferenceSolution] = useState("");
  const [showReference, setShowReference] = useState(false);
  const [rubric, setRubric] = useState(cloneDefaultRubric);
  const [rubricIsDefault, setRubricIsDefault] = useState(true);

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [saving, setSaving] = useState(false);

  const dirty = !blank(title) || !blank(description);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && phase !== PHASE.GENERATING && !saving) handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, saving, dirty]);

  const handleClose = () => {
    if (dirty && !window.confirm("Discard this problem? Unsaved changes will be lost.")) return;
    onClose();
  };

  const toggleTopic = (t) =>
    setTopics((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const goToReview = () => {
    const errs = validateDescribe({ title, description, topics });
    setErrors(errs);
    if (Object.keys(errs).length) return false;
    setApiError("");
    setPhase(PHASE.REVIEW);
    return true;
  };

  const handleGenerate = async () => {
    const errs = validateDescribe({ title, description, topics: topics.length ? topics : ["_"] });
    delete errs.topics; // topics may be suggested by the AI, so they are optional before generating
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setApiError("");
    setPhase(PHASE.GENERATING);
    try {
      const res = await axios.post(
        `${host}/api/generate_coding_problem`,
        { title: title.trim(), description: description.trim(), track: "apex" },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const d = res.data || {};
      if (!topics.length && Array.isArray(d.topics) && d.topics.length) setTopics(d.topics.filter((t) => APEX_TOPICS.includes(t)));
      if (d.starter_code) setStarterCode(d.starter_code);
      if (d.reference_solution) { setReferenceSolution(d.reference_solution); setShowReference(true); }
      if (Array.isArray(d.rubric) && d.rubric.length) setRubric(d.rubric);
      setRubricIsDefault(Boolean(d.rubric_is_default));
      setPhase(PHASE.REVIEW);
    } catch {
      setPhase(PHASE.DESCRIBE);
      setApiError("AI draft failed. You can try again or fill in the details manually.");
    }
  };

  // ── Rubric editing ──────────────────────────────────────────────────────────
  const updateCriterion = (idx, patch) =>
    setRubric((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addCriterion = () =>
    setRubric((prev) => [...prev, { id: `custom_${Date.now()}`, name: "", description: "", weight: 0 }]);
  const removeCriterion = (idx) => setRubric((prev) => prev.filter((_, i) => i !== idx));
  const resetRubric = () => { setRubric(cloneDefaultRubric()); setRubricIsDefault(true); };

  const total = Math.round(rubricTotal(rubric));
  const rubricError = validateRubric(rubric);

  const handleSave = async () => {
    const describeErrs = validateDescribe({ title, description, topics });
    if (Object.keys(describeErrs).length) { setErrors(describeErrs); setPhase(PHASE.DESCRIBE); return; }
    if (rubricError) { setErrors({ rubric: rubricError }); return; }
    setErrors({});
    setApiError("");
    setSaving(true);
    const result = await onSubmit({
      track: "apex",
      title: title.trim(),
      description: description.trim(),
      difficulty,
      topics,
      starter_code: starterCode,
      reference_solution: referenceSolution,
      rubric: rubric.map((c) => ({ id: c.id, name: c.name.trim(), description: (c.description || "").trim(), weight: Number(c.weight) })),
    });
    // On success the parent unmounts this modal, so only reset state on failure.
    if (!result?.ok) {
      setSaving(false);
      setApiError(result?.error || "Could not save the problem. Please try again.");
    }
  };

  const phaseIdx = phase === PHASE.REVIEW ? 1 : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
        style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
        onClick={() => { if (phase !== PHASE.GENERATING && !saving) handleClose(); }}
      />
      <div
        className="w-full max-w-3xl flex flex-col max-h-[90vh] relative bg-bgSecondary rounded-2xl shadow-2xl border border-white/10 animate-[fadeInScale_0.2s_ease-out]"
        style={{ background: theme === "dark" ? "linear-gradient(to bottom right, rgba(15,18,42,0.94), rgba(10,9,28,0.90))" : "" }}
      >
        {/* Stepper */}
        <div className="flex-shrink-0 flex items-center px-6 pt-4 pb-1">
          {PHASE_LABELS.map((label, i) => {
            const isDone = i < phaseIdx;
            const isActive = i === phaseIdx;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border ${isDone ? "bg-[#069855]/10 border-[#069855] text-[#069855]" : isActive ? "border-[#069855] text-[#069855]" : "border-gray-300 dark:border-white/20 text-gray-400"}`}>
                    {isDone ? "✓" : i + 1}
                  </div>
                  <span className={`text-[11px] font-medium ${isDone || isActive ? "text-[#069855]" : "text-gray-400 dark:text-gray-500"}`}>{label}</span>
                </div>
                {i < PHASE_LABELS.length - 1 && <div className="flex-1 mx-2 h-px bg-gray-200 dark:bg-white/10" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 pt-3 pb-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold dark:text-white text-textPrimary" style={{ fontFamily: "Poppins, sans-serif" }}>
              {phase === PHASE.REVIEW ? "Starter code & grading rubric" : "Add a custom Apex question"}
            </h2>
            <p className="text-xs dark:text-gray-400 text-textSecondary mt-0.5">
              {phase === PHASE.DESCRIBE && "Candidates write Apex; AI reviews it against the rubric you define. Code is not executed."}
              {phase === PHASE.GENERATING && "AI is drafting starter code and a rubric…"}
              {phase === PHASE.REVIEW && "Everything is editable. The rubric decides how answers are scored."}
            </p>
          </div>
          {phase !== PHASE.GENERATING && (
            <button onClick={handleClose} disabled={saving} className="w-8 h-8 rounded-full flex items-center justify-center dark:text-gray-400 text-textSecondary hover:text-red-400 hover:bg-red-400/10 transition-all" aria-label="Close">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {phase === PHASE.DESCRIBE && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">Title <span className="text-red-400">*</span></label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus
                  placeholder="e.g. Bulk-safe Case escalation trigger" className={inputCls(errors.title)} />
                {errors.title && <p className="text-red-400 text-xs">{errors.title}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">Problem statement <span className="text-red-400">*</span></label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={7} maxLength={8000}
                  placeholder="Describe the requirement precisely: objects and fields involved, expected behaviour, edge cases, and any constraints (bulk size, governor limits…)."
                  className={`${inputCls(errors.description)} resize-none`} />
                <div className="flex justify-between">
                  {errors.description ? <p className="text-red-400 text-xs">{errors.description}</p> : <p className="text-[11px] text-gray-400 dark:text-gray-500">Min 20 characters. Mention any custom objects or fields the candidate should assume exist.</p>}
                  <span className="text-[11px] tabular-nums text-gray-400 dark:text-gray-500">{description.length}/8000</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">Difficulty</label>
                <div className="flex gap-2">
                  {APEX_DIFFICULTIES.map((d) => (
                    <button key={d} type="button" onClick={() => setDifficulty(d)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${difficulty === d ? DIFF_STYLE[d] : "border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">Topics <span className="text-red-400">*</span></label>
                <div className="flex flex-wrap gap-2">
                  {APEX_TOPICS.map((t) => (
                    <button key={t} type="button" onClick={() => toggleTopic(t)} aria-pressed={topics.includes(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${topics.includes(t) ? "border-violet-400 bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-300" : "border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-violet-300"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {errors.topics && <p className="text-red-400 text-xs">{errors.topics}</p>}
              </div>

              {apiError && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
                  <AlertTriangle size={16} className="text-rose-500 flex-shrink-0" />
                  <p className="text-sm text-rose-600 dark:text-rose-400 flex-1">{apiError}</p>
                </div>
              )}
            </div>
          )}

          {phase === PHASE.GENERATING && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))" }}>
                <Sparkles size={28} className="text-blue-500 dark:text-blue-400 animate-pulse" />
              </div>
              <p className="text-sm dark:text-white text-gray-700">Drafting starter code, reference solution and rubric…</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">This takes about 10–20 seconds</p>
            </div>
          )}

          {phase === PHASE.REVIEW && (
            <div className="space-y-6">
              {/* Starter code */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">Starter code shown to the candidate</label>
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <Editor theme="vs-dark" height="220px" language="java" value={starterCode}
                    onChange={(v) => setStarterCode(v ?? "")}
                    options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, padding: { top: 10, bottom: 10 } }} />
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">Do not include the solution. An answer identical to this starter code scores 0.</p>
              </div>

              {/* Reference solution */}
              <div className="space-y-2">
                <button type="button" onClick={() => setShowReference((s) => !s)} className="text-sm font-semibold dark:text-white text-textPrimary hover:text-violet-500 transition-colors">
                  {showReference ? "▾" : "▸"} Reference solution <span className="text-xs font-normal text-gray-400">(optional, never shown to candidates; helps the AI grade)</span>
                </button>
                {showReference && (
                  <div className="rounded-xl overflow-hidden border border-white/10">
                    <Editor theme="vs-dark" height="220px" language="java" value={referenceSolution}
                      onChange={(v) => setReferenceSolution(v ?? "")}
                      options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, padding: { top: 10, bottom: 10 } }} />
                  </div>
                )}
              </div>

              {/* Rubric */}
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <label className="block text-sm font-semibold dark:text-white text-textPrimary">Grading rubric</label>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">AI scores each criterion 0–100; the final score is the weighted average.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${total === 100 ? "border-emerald-300 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10" : "border-rose-300 text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10"}`}>
                      Total {total}/100
                    </span>
                    <button type="button" onClick={resetRubric} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-violet-300 transition-colors">
                      <RotateCcw size={10} />Reset to default
                    </button>
                  </div>
                </div>

                {rubricIsDefault && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">This is the generic default rubric. Tailor it to your question for fairer scoring.</p>
                )}

                <div className="space-y-2">
                  {rubric.map((c, idx) => (
                    <div key={c.id || idx} className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.03] p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input type="text" value={c.name} onChange={(e) => updateCriterion(idx, { name: e.target.value })} maxLength={100}
                          placeholder="Criterion name" aria-label={`Criterion ${idx + 1} name`}
                          className="flex-1 min-w-0 bg-transparent border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm dark:text-white text-textPrimary focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <input type="number" min={1} max={100} value={c.weight}
                            onChange={(e) => updateCriterion(idx, { weight: e.target.value === "" ? "" : Number(e.target.value) })}
                            aria-label={`Criterion ${idx + 1} weight`}
                            className="w-16 bg-transparent border border-gray-200 dark:border-white/10 rounded-lg px-2 py-2 text-sm text-center dark:text-white text-textPrimary focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
                          <span className="text-xs text-gray-400">%</span>
                        </div>
                        <button type="button" onClick={() => removeCriterion(idx)} aria-label={`Remove criterion ${idx + 1}`}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-gray-300 dark:text-gray-600 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all flex-shrink-0">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <textarea value={c.description || ""} onChange={(e) => updateCriterion(idx, { description: e.target.value })} rows={2} maxLength={500}
                        placeholder="What does a strong answer do for this criterion?" aria-label={`Criterion ${idx + 1} description`}
                        className="w-full bg-transparent border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-xs dark:text-gray-300 text-textSecondary resize-none focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
                    </div>
                  ))}
                </div>

                <button type="button" onClick={addCriterion}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-gray-300 dark:border-white/[0.15] text-gray-400 dark:text-gray-500 hover:border-violet-300 hover:text-violet-500 transition-all text-xs font-medium">
                  <Plus size={12} /> Add criterion
                </button>

                {(errors.rubric || (rubricError && total !== 100)) && (
                  <p className="text-red-400 text-xs">{errors.rubric || rubricError}</p>
                )}
              </div>

              {apiError && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
                  <AlertTriangle size={16} className="text-rose-500 flex-shrink-0" />
                  <p className="text-sm text-rose-600 dark:text-rose-400 flex-1">{apiError}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {phase !== PHASE.GENERATING && (
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
            {phase === PHASE.REVIEW ? (
              <>
                <button type="button" onClick={() => setPhase(PHASE.DESCRIBE)} disabled={saving}
                  className="border-btn dark:border-btn-dark flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all disabled:opacity-40">
                  <ArrowLeft size={14} />Back
                </button>
                <button type="button" onClick={handleSave} disabled={saving} className="button flex items-center gap-2 px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                  {saving ? <><Loader2 size={14} className="animate-spin" /><span>Saving…</span></> : <span>Save question</span>}
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={handleClose} className="border-btn dark:border-btn-dark px-5 py-2.5 rounded-full text-sm font-medium transition-all">Cancel</button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={goToReview} className="border-btn dark:border-btn-dark px-5 py-2.5 rounded-full text-sm font-medium transition-all">Fill in manually</button>
                  <button type="button" onClick={handleGenerate} className="button flex items-center gap-2 px-6 py-2.5">
                    <Sparkles size={14} /><span>Draft with AI</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HostApexProblem;
